/**
 * Isolated daily recurring countdown (announcement bar / page).
 * Mutates only digit text nodes inside this element — never attributes,
 * never ancestors, never layout-affecting style.
 */
class CountdownTimerComponent extends HTMLElement {
  static get observedAttributes() {
    return ['data-hour', 'data-minute', 'data-timezone'];
  }

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;
  /** @type {HTMLElement | null} */
  #hoursEl = null;
  /** @type {HTMLElement | null} */
  #minutesEl = null;
  /** @type {HTMLElement | null} */
  #secondsEl = null;
  /** @type {string} */
  #lastKey = '';

  connectedCallback() {
    this.#cacheRefs();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
  }

  /**
   * @param {string} name
   */
  attributeChangedCallback(name) {
    if (!this.isConnected) return;
    if (name === 'data-hour' || name === 'data-minute' || name === 'data-timezone') {
      this.#lastKey = '';
      this.#tick();
    }
  }

  #cacheRefs() {
    this.#hoursEl = this.querySelector(':scope [data-countdown-timer-hours]');
    this.#minutesEl = this.querySelector(':scope [data-countdown-timer-minutes]');
    this.#secondsEl = this.querySelector(':scope [data-countdown-timer-seconds]');
  }

  #start() {
    this.#stop();
    this.#tick();
    this.#interval = setInterval(() => {
      if (!this.isConnected) {
        this.#stop();
        return;
      }
      this.#tick();
    }, 1000);
  }

  #stop() {
    if (this.#interval !== undefined) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  get #targetHour() {
    const value = Number.parseInt(this.getAttribute('data-hour') ?? '20', 10);
    return Number.isNaN(value) ? 20 : Math.min(23, Math.max(0, value));
  }

  get #targetMinute() {
    const value = Number.parseInt(this.getAttribute('data-minute') ?? '0', 10);
    return Number.isNaN(value) ? 0 : Math.min(59, Math.max(0, value));
  }

  get #timeZone() {
    return this.getAttribute('data-timezone') || 'America/New_York';
  }

  /**
   * @param {Date} date
   * @param {string} timeZone
   */
  #getZonedParts(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);

    const get = (/** @type {Intl.DateTimeFormatPartTypes} */ type) => {
      const part = parts.find((entry) => entry.type === type);
      return Number.parseInt(part?.value ?? '0', 10);
    };

    return {
      hour: get('hour') % 24,
      minute: get('minute'),
      second: get('second'),
    };
  }

  #remainingSeconds() {
    const now = this.#getZonedParts(new Date(), this.#timeZone);
    const nowSeconds = now.hour * 3600 + now.minute * 60 + now.second;
    const targetSeconds = this.#targetHour * 3600 + this.#targetMinute * 60;
    let remaining = targetSeconds - nowSeconds;

    if (remaining <= 0) {
      remaining += 24 * 3600;
    }

    return remaining;
  }

  /**
   * @param {number} value
   */
  #pad(value) {
    return String(value).padStart(2, '0');
  }

  /**
   * @param {HTMLElement | null} el
   * @param {string} next
   */
  #setText(el, next) {
    if (!el || el.textContent === next) return;
    el.textContent = next;
  }

  #tick() {
    const remaining = this.#remainingSeconds();
    const hours = this.#pad(Math.floor(remaining / 3600));
    const minutes = this.#pad(Math.floor((remaining % 3600) / 60));
    const seconds = this.#pad(remaining % 60);
    const key = `${hours}:${minutes}:${seconds}`;

    if (key === this.#lastKey) return;
    this.#lastKey = key;

    this.#setText(this.#hoursEl, hours);
    this.#setText(this.#minutesEl, minutes);
    this.#setText(this.#secondsEl, seconds);
    this.setAttribute('data-ready', '');
  }
}

if (!customElements.get('countdown-timer-component')) {
  customElements.define('countdown-timer-component', CountdownTimerComponent);
}
